import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { profileService } from "@/services/profile";
import type { Plan } from "@/services/profile";
import { billingService } from "@/services/billing";
import { MercadoPagoWalletBrick } from "@/components/billing/MercadoPagoFields";
import { RevenueCatSubscriptionCard } from "@/components/billing/RevenueCatSubscriptionCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  User,
  Mail,
  Building2,
  Calendar,
  CreditCard,
  CheckCircle2,
  Shield,
  Users,
  Tv,
  Clock,
  FileText,
  Zap,
  Star,
  BadgeCheck,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { MessagingCard } from "@/components/messaging/MessagingCard";
import { DataImportExportCard } from "@/components/data-io/DataImportExportCard";

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatCurrency(amount: string, currency: string) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(parseFloat(amount));
}

function getInitials(firstName: string, lastName: string) {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
}

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  ACTIVE: { label: "Activo", className: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  TRIAL: { label: "Prueba", className: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  INACTIVE: { label: "Inactivo", className: "bg-muted text-muted-foreground border-border" },
  CANCELLED: { label: "Cancelado", className: "bg-destructive/15 text-destructive border-destructive/30" },
};

const BILLING_STATUS_MAP: Record<string, { label: string; className: string }> = {
  READY: { label: "Pagado", className: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  COMPLETED: { label: "Pagado", className: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  PENDING: { label: "Pendiente", className: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  FAILED: { label: "Fallido", className: "bg-destructive/15 text-destructive border-destructive/30" },
};

const PAYMENT_METHOD_MAP: Record<string, string> = {
  CARD: "Tarjeta",
  TRANSFER: "Transferencia",
  CASH: "Efectivo",
  MERCADOPAGO: "MercadoPago",
};

const PLAN_COLOR_MAP: Record<string, { accent: string; bg: string; border: string }> = {
  BASIC: {
    accent: "text-sky-400",
    bg: "bg-sky-500/10",
    border: "border-sky-500/30",
  },
  PRO: {
    accent: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/30",
  },
  ENTERPRISE: {
    accent: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
  },
};

const ROL_LABEL: Record<string, string> = {
  OWNER: "Propietario",
  ADMIN: "Administrador",
  COLLABORATOR: "Colaborador",
};

export default function Profile() {
  const queryClient = useQueryClient();

  // ── State ──────────────────────────────────────────────────────────────────
  const [subscribeDialog, setSubscribeDialog] = useState<{
    open: boolean;
    plan: Plan | null;
  }>({ open: false, plan: null });
  const [billingCycle, setBillingCycle] = useState<"MONTHLY" | "YEARLY">("MONTHLY");
  const [preferenceId, setPreferenceId] = useState<string | null>(null);
  const [loadingPreference, setLoadingPreference] = useState(false);

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: profileData, isLoading: loadingProfile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => profileService.getProfile(),
  });

  const { data: subscriptionData, isLoading: loadingSubscription } = useQuery({
    queryKey: ["subscription"],
    queryFn: () => profileService.getSubscription(),
  });

  const { data: plansData, isLoading: loadingPlans } = useQuery({
    queryKey: ["plans"],
    queryFn: () => profileService.getPlans(),
  });

  const { data: billingData, isLoading: loadingBilling } = useQuery({
    queryKey: ["billing-history"],
    queryFn: () => profileService.getBillingHistory(),
  });

  const { data: mpStatusData } = useQuery({
    queryKey: ["mp-status"],
    queryFn: async () => {
      try {
        const res = await billingService.getMpStatus();
        return res.data ?? null;
      } catch {
        return null;
      }
    },
    retry: false,
  });

  // ── Mutations ──────────────────────────────────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: billingService.cancelMercadoPago,
    onSuccess: () => {
      toast.success("Suscripción cancelada exitosamente.");
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      queryClient.invalidateQueries({ queryKey: ["mp-status"] });
    },
    onError: () => toast.error("Error al cancelar la suscripción."),
  });

  // ── Derived state ──────────────────────────────────────────────────────────
  const user = profileData?.data;
  const sub = subscriptionData?.data;
  const plans = plansData?.data ?? [];
  const billing = billingData?.data ?? [];
  const mpStatus = mpStatusData;
  const hasMpActive = mpStatus?.status === "ACTIVE" || mpStatus?.status === "PENDING";

  const isLoading = loadingProfile || loadingSubscription || loadingPlans || loadingBilling;

  const openSubscribeDialog = (plan: Plan) => {
    setSubscribeDialog({ open: true, plan });
    setBillingCycle("MONTHLY");
    setPreferenceId(null);
  };

  // Create a new preference whenever the dialog is open and billing cycle changes
  useEffect(() => {
    if (!subscribeDialog.open || !subscribeDialog.plan) return;
    let cancelled = false;
    setPreferenceId(null);
    setLoadingPreference(true);
    billingService
      .createMpPreference({ planId: subscribeDialog.plan.id, billingCycle })
      .then((res) => {
        if (!cancelled) setPreferenceId(res.data?.preferenceId ?? null);
      })
      .catch(() => {
        if (!cancelled) toast.error("Error al preparar el pago. Intenta de nuevo.");
      })
      .finally(() => {
        if (!cancelled) setLoadingPreference(false);
      });
    return () => { cancelled = true; };
  }, [subscribeDialog.open, subscribeDialog.plan?.id, billingCycle]);

  if (isLoading) {
    return (
      <DashboardLayout title="Mi Perfil">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Cargando perfil...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const subStatus = sub?.subscription?.status ?? "";
  const statusInfo = STATUS_MAP[subStatus] ?? { label: subStatus, className: "bg-muted text-muted-foreground border-border" };
  const currentPlanId = sub?.plan?.id;

  return (
    <DashboardLayout title="Mi Perfil" subtitle="Información de tu cuenta y suscripción">
      <div className="space-y-6 pb-10">

        {/* ── Hero Section ── */}
        <div className="relative overflow-hidden rounded-xl border border-border bg-card">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 50%, hsl(var(--primary)) 0%, transparent 60%), radial-gradient(circle at 80% 20%, hsl(var(--primary)) 0%, transparent 50%)",
            }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 p-6">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="w-20 h-20 rounded-2xl bg-primary/15 border border-primary/25 flex items-center justify-center shadow-inner">
                <span className="text-3xl font-bold text-primary tracking-tight">
                  {user ? getInitials(user.firstName, user.lastName) : "—"}
                </span>
              </div>
              <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-emerald-500 border-2 border-card flex items-center justify-center">
                <CheckCircle2 className="w-3.5 h-3.5 text-white" />
              </div>
            </div>

            {/* Name & role */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2.5 mb-1">
                <h2 className="text-2xl font-bold text-foreground tracking-tight">
                  {user ? `${user.firstName} ${user.lastName}` : "—"}
                </h2>
                {user?.rol && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                    <BadgeCheck className="w-3 h-3" />
                    {ROL_LABEL[user.rol] ?? user.rol}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground text-sm truncate">{user?.email}</p>
              <p className="text-muted-foreground/70 text-xs mt-0.5 font-mono">
                {user?.account?.accountCode}
              </p>
            </div>

            {/* Subscription badge */}
            <div className="shrink-0 flex flex-col items-end gap-1.5">
              {sub && (
                <>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border ${statusInfo.className}`}
                  >
                    <Clock className="w-3.5 h-3.5" />
                    {statusInfo.label}
                  </span>
                  <span className="text-xs text-muted-foreground font-medium">
                    Plan {sub.plan?.name}
                  </span>
                  {hasMpActive && (
                    <span className="inline-flex items-center gap-1 text-xs text-blue-400 font-medium">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                      MercadoPago activo
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Info Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Personal Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="w-4 h-4 text-primary" />
                Información personal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow icon={<User className="w-3.5 h-3.5" />} label="Nombre completo"
                value={user ? `${user.firstName} ${user.lastName}` : "—"} />
              <Separator />
              <InfoRow icon={<Mail className="w-3.5 h-3.5" />} label="Correo electrónico"
                value={user?.email ?? "—"} />
              <Separator />
              <InfoRow icon={<Shield className="w-3.5 h-3.5" />} label="Rol"
                value={user?.rol ? (ROL_LABEL[user.rol] ?? user.rol) : "—"} />
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="w-4 h-4 text-primary" />
                Información de cuenta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow icon={<Building2 className="w-3.5 h-3.5" />} label="Nombre de cuenta"
                value={user?.account?.nameAccount ?? "—"} />
              <Separator />
              <InfoRow icon={<FileText className="w-3.5 h-3.5" />} label="Código"
                value={user?.account?.accountCode ?? "—"} mono />
              <Separator />
              <InfoRow icon={<Mail className="w-3.5 h-3.5" />} label="Email de cuenta"
                value={user?.account?.email ?? "—"} />
              <Separator />
              <InfoRow icon={<Calendar className="w-3.5 h-3.5" />} label="Creada"
                value={formatDate(user?.account?.createdAt ?? null)} />
            </CardContent>
          </Card>
        </div>

        {/* ── Current Subscription ── */}
        {sub && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="w-4 h-4 text-primary" />
                Suscripción actual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Sub details */}
                <div className="space-y-3 md:col-span-1">
                  <InfoRow icon={<Calendar className="w-3.5 h-3.5" />} label="Inicio"
                    value={formatDate(sub.subscription?.startDate)} />
                  <Separator />
                  {sub.subscription?.trialEndsAt && (
                    <>
                      <InfoRow icon={<Clock className="w-3.5 h-3.5" />} label="Fin del trial"
                        value={formatDate(sub.subscription.trialEndsAt)} />
                      <Separator />
                    </>
                  )}
                  <InfoRow icon={<CreditCard className="w-3.5 h-3.5" />} label="Ciclo"
                    value={sub.subscription?.billingCycle === "MONTHLY" ? "Mensual" : "Anual"} />
                </div>

                {/* Plan limits */}
                <div className="md:col-span-1 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Límites del plan
                  </p>
                  <LimitRow icon={<Users className="w-3.5 h-3.5 text-sky-400" />}
                    label="Clientes" value={sub.plan?.maxClients} />
                  <LimitRow icon={<Tv className="w-3.5 h-3.5 text-violet-400" />}
                    label="Cuentas streaming" value={sub.plan?.maxStreamingAccounts} />
                  <LimitRow icon={<Shield className="w-3.5 h-3.5 text-emerald-400" />}
                    label="Colaboradores" value={sub.plan?.maxCollaborators} />
                </div>

                {/* Features */}
                <div className="md:col-span-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Funcionalidades
                  </p>
                  <div className="space-y-2">
                    {sub.features?.map((f) => (
                      <div key={f.id} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground leading-tight">{f.name}</p>
                          <p className="text-xs text-muted-foreground">{f.description}</p>
                        </div>
                        {f.isPremium && (
                          <Star className="w-3 h-3 text-amber-400 mt-1 shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── RevenueCat (Vendeplay Pro) ── */}
        <RevenueCatSubscriptionCard
          appUserId={user?.account?.accountCode}
          customerEmail={user?.account?.email ?? user?.email}
        />

        {/* ── Message Templates & Billing Rules ── */}
        <MessagingCard />

        {/* ── Data Import / Export ── */}
        <DataImportExportCard />

        {/* ── Billing History ── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="w-4 h-4 text-primary" />
              Historial de facturación
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billing.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <FileText className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Sin registros de facturación</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2.5 pr-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Factura
                      </th>
                      <th className="text-left py-2.5 pr-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Período
                      </th>
                      <th className="text-left py-2.5 pr-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Método
                      </th>
                      <th className="text-right py-2.5 pr-4 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Monto
                      </th>
                      <th className="text-right py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {billing.map((record) => {
                      const bStatus = BILLING_STATUS_MAP[record.status] ?? {
                        label: record.status,
                        className: "bg-muted text-muted-foreground border-border",
                      };
                      return (
                        <tr key={record.id} className="hover:bg-muted/30 transition-colors">
                          <td className="py-3 pr-4">
                            <span className="font-mono text-xs font-medium text-foreground">
                              {record.invoiceNumber}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-muted-foreground text-xs">
                            {formatDate(record.billingPeriodStart)} — {formatDate(record.billingPeriodEnd)}
                          </td>
                          <td className="py-3 pr-4 text-muted-foreground text-xs">
                            {PAYMENT_METHOD_MAP[record.paymentMethod] ?? record.paymentMethod}
                          </td>
                          <td className="py-3 pr-4 text-right font-semibold text-foreground">
                            {formatCurrency(record.amount, record.currency)}
                          </td>
                          <td className="py-3 text-right">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${bStatus.className}`}
                            >
                              {bStatus.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Subscribe Dialog ── */}
      <Dialog
        open={subscribeDialog.open}
        onOpenChange={(open) => {
          setSubscribeDialog({ open, plan: subscribeDialog.plan });
          if (!open) setPreferenceId(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="pb-1">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Zap className="w-4 h-4 text-primary" />
              Suscribirse — {subscribeDialog.plan?.name}
            </DialogTitle>
          </DialogHeader>

          {subscribeDialog.plan && (() => {
            const plan = subscribeDialog.plan;
            const colors = PLAN_COLOR_MAP[plan.name] ?? {
              accent: "text-primary",
              bg: "bg-primary/10",
              border: "border-primary/20",
            };

            return (
              <>
                {/* Plan summary + billing cycle */}
                <div className={`rounded-lg border px-3 py-2.5 mb-3 ${colors.bg} ${colors.border} flex items-center justify-between gap-3`}>
                  <div>
                    <span className={`text-sm font-bold ${colors.accent}`}>{plan.name}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {billingCycle === "MONTHLY"
                        ? `$${parseFloat(plan.priceMonthly).toFixed(0)} / mes`
                        : `$${parseFloat(plan.priceYearly).toFixed(2)} / año`}
                    </p>
                  </div>
                  <div className="flex gap-1 p-0.5 rounded-md bg-muted/60 border border-border shrink-0">
                    {(["MONTHLY", "YEARLY"] as const).map((cycle) => (
                      <button
                        key={cycle}
                        type="button"
                        onClick={() => setBillingCycle(cycle)}
                        className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                          billingCycle === cycle
                            ? "bg-card shadow-sm text-foreground border border-border"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {cycle === "MONTHLY" ? "Mensual" : "Anual"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wallet Brick */}
                {loadingPreference ? (
                  <div className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Preparando pago...
                  </div>
                ) : preferenceId ? (
                  <MercadoPagoWalletBrick key={preferenceId} preferenceId={preferenceId} />
                ) : null}
              </>
            );
          })()}

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSubscribeDialog({ open: false, plan: null }); setPreferenceId(null); }}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function InfoRow({
  icon,
  label,
  value,
  mono = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-muted-foreground min-w-0">
        <span className="shrink-0">{icon}</span>
        <span className="text-xs">{label}</span>
      </div>
      <span
        className={`text-sm font-medium text-foreground truncate text-right ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function LimitRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0">{icon}</span>
      <span className="text-xs text-muted-foreground flex-1">{label}</span>
      <span className="text-sm font-bold text-foreground tabular-nums">{value}</span>
    </div>
  );
}

function PlanLimit({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="text-muted-foreground/60 shrink-0">{icon}</span>
      {label}
    </div>
  );
}
